import cuid from "cuid";
import dayjs from "dayjs";
import { get } from "lodash";
import format from "string-template";
import { DATE_FORMAT } from "utils/constants";
import * as yup from "yup";

export const ACORIS_TERMINATION_MIN_DELAY_DAYS = 30;

export const acorisMinTerminationDate = () =>
  dayjs().startOf("day").add(ACORIS_TERMINATION_MIN_DELAY_DAYS, "days");

export const acorisTerminationDateSchema = (
  messages,
  { required = true } = {},
) => {
  const minDate = acorisMinTerminationDate();
  const schema = yup
    .date()
    .typeError(messages.REQUIRED)
    .nullable()
    .min(
      minDate.toDate(),
      messages.MIN_DATE &&
        format(messages.MIN_DATE, { date: minDate.format(DATE_FORMAT) }),
    );
  return required ? schema.required(messages.REQUIRED) : schema;
};

export const resolveAcorisHasOtherInsurer = (project) => {
  const contactFields = get(project, "contact.fields", {});
  return (
    contactFields.hasOtherInsurer ??
    get(project, "fields.hasOtherInsurer") ??
    get(project, "fields.replaceContract") ??
    project?.ria === true
  );
};

export const resolveAcorisContractMoreThanOneYear = (project) => {
  const contactFields = get(project, "contact.fields", {});
  return (
    get(project, "fields.contractMoreThanOneYear") ??
    contactFields.contractOlderThanOneYear ??
    project?.ria === true
  );
};

export const resolveAcorisTerminationRequest = (project) => {
  const existing = get(project, "terminationRequest");
  const contactFields = get(project, "contact.fields", {});

  if (
    existing?.dueDate ||
    existing?.contractNumber ||
    existing?.recipient ||
    existing?.brokerageMandate?.currentCompany
  ) {
    return existing;
  }

  const dueDate = contactFields.currentContractEffectDate;
  const contractNumber = contactFields.currentContractReference;
  const insurerName = contactFields.currentInsurerName;

  if (!dueDate && !contractNumber && !insurerName) {
    return existing || null;
  }

  return {
    ...existing,
    id: existing?.id || cuid(),
    dueDate,
    contractNumber,
    recipient: existing?.recipient || null,
    reason: existing?.reason || "22",
    brokerageMandate: {
      id: get(existing, "brokerageMandate.id") || cuid(),
      currentCompany:
        insurerName || get(existing, "brokerageMandate.currentCompany"),
      firstName: get(existing, "brokerageMandate.firstName"),
      lastName: get(existing, "brokerageMandate.lastName"),
    },
  };
};

export const buildAcorisTerminationRequestPayload = ({
  terminationRequest,
  contact,
  isRia,
}) => {
  if (!isRia) {
    return null;
  }

  return {
    id: get(terminationRequest, "id") || cuid(),
    dueDate: get(terminationRequest, "dueDate"),
    terminationDate: get(terminationRequest, "terminationDate"),
    contractNumber: get(terminationRequest, "contractNumber"),
    recipient: get(terminationRequest, "recipient"),
    reason: get(terminationRequest, "reason") || "22",
    address: get(terminationRequest, "address"),
    complement: get(terminationRequest, "complement"),
    zipCode: get(terminationRequest, "zipCode"),
    city: get(terminationRequest, "city"),
    brokerageMandate: {
      id: get(terminationRequest, "brokerageMandate.id") || cuid(),
      currentCompany: get(
        terminationRequest,
        "brokerageMandate.currentCompany",
      ),
      firstName:
        get(terminationRequest, "brokerageMandate.firstName") ||
        get(contact, "user.firstname"),
      lastName:
        get(terminationRequest, "brokerageMandate.lastName") ||
        get(contact, "user.lastname"),
    },
  };
};

export const createEmptyAcorisTerminationRequest = () => ({
  id: cuid(),
  reason: "22",
  brokerageMandate: {
    id: cuid(),
  },
});
